/**
 * Coverage tab stack for the EV coverage screen: tier tabs, unified EV layout,
 * and modals driven from Postgres-backed props (no Firestore order slice).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import type { CoverageInformation } from "../../../../data-model/coverageInformation";
import {
  AuthOnFile,
  Demographics,
  GeneralOrderDetails,
  SingleCoverage,
  Status,
} from "../../../../data-model/medicalPaOrdersModel";
import {
  ManualEvEditClickSource,
  ScreenNamesForBigQuery,
} from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import { Submission } from "../../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { useValidateBvEnabled } from "../../../../hooks/useValidateBvEnabled";
import { useValidateCopayCoinsuranceEnabled } from "../../../../hooks/useValidateCopayCoinsuranceEnabled";
import { useValidateEvEnabled } from "../../../../hooks/useValidateEvEnabled";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { EV_COVERAGE_REQUIRE_FIELDS_ON_SAVE } from "../../config/evCoverageEditFlags";
import { isCoverageRowManuallyUpdated } from "../../helpers/coverageManualUpdate";
import {
  buildManualEvEditClickedEvent,
  buildManualEvFieldEditedEvent,
  buildManualEvOverrideSavedEvent,
  type ManualEvFieldEditedDetail,
} from "../../helpers/evCoverageAnalyticsEvents";
import {
  shouldAutoEnterEditModeOnContinueValidationFailure,
  shouldRunEvCoverageContinueValidationForDateOfWork,
  validateAllEvCoverageTabsForContinue,
  type EvCoverageContinueValidationEntry,
  type ValidateAllEvCoverageTabsResult,
} from "../../helpers/evCoverageEditValidation";
import { useDebouncedManualEvFieldEdited } from "../../hooks/useDebouncedManualEvFieldEdited";
import EvCoverageEmptyState from "../evCoverageEmptyState/evCoverageEmptyState";
import { EvCoverageInsuranceTierTabBar } from "../evCoverageInsuranceTierTabBar/evCoverageInsuranceTierTabBar";
import EvCoverageUnifiedContent, {
  type EvCoverageUnifiedContentHandle,
} from "../evCoverageUnifiedContent/evCoverageUnifiedContent";
import CoverageModals from "./coverageModals";
import { useCoverageAnalytics } from "./hooks/useCoverageAnalytics";
import { useHappyFlowSave } from "./hooks/useHappyFlowSave";
import { useInactiveInsuranceWriteBack } from "./hooks/useInactiveInsuranceWriteBack";

/** Props for the Coverage component. */
interface CoverageProps {
  coverageInformation: CoverageInformation;
  currentTabIndex?: (index: number) => void;
  demographicsData: Demographics;
  authOnFileDetails: AuthOnFile;
  submissionData: Submission;
  generalOrderDetails?: GeneralOrderDetails;
  status: Status;
  coverage: SingleCoverage[];
  orderId: string;
  orgId: string;
  serviceTypeCodeLabelMap?: ReadonlyMap<string, string>;
  onCoverageSaved?: () => void;
  /** Called when the active tier enters or leaves local EV edit mode. */
  onEditingChange?: (isEditing: boolean) => void;
  /** True when any returned coverage row was manually edited by a user (bypasses status-gated views). */
  isManuallyUpdated: boolean;
}

/** Imperative handle for Continue validation across all insurance tabs. */
export interface CoverageHandle {
  validateForContinue: () => ValidateAllEvCoverageTabsResult | null;
}

/**
 * Renders Postgres-backed coverage tiers with EV unified content and Redux-driven modals.
 *
 * @param props - Coverage rows, auth-on-file, submission, and order context
 * @returns Coverage tabs and modal subtree
 */
export const Coverage = forwardRef<CoverageHandle, CoverageProps>(
  (props, ref) => {
    const {
      coverageInformation,
      currentTabIndex,
      demographicsData,
      authOnFileDetails,
      submissionData,
      generalOrderDetails,
      status,
      coverage,
      orderId,
      orgId,
      serviceTypeCodeLabelMap,
      onCoverageSaved,
      onEditingChange,
    } = props;

    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);
    const [continueValidationAttemptId, setContinueValidationAttemptId] =
      useState(0);
    const [autoEnterEditOnContinueFailure, setAutoEnterEditOnContinueFailure] =
      useState(false);
    const [focusTreatmentTabIndex, setFocusTreatmentTabIndex] = useState<
      number | undefined
    >(undefined);
    const isEventTriggerRef = useRef(false);
    const activeUnifiedContentRef =
      useRef<EvCoverageUnifiedContentHandle>(null);

    const { user } = useSelector(
      (state: RootState) => state.firebaseAuthentication,
    );
    const org_id = getOrgIdForFetchExternalWorklist();
    const { openedModalId } = useSelector((state: RootState) => state.modal);
    const { payerName } = usePayerNameContextFromSingleOrderLayout();
    const requireBvFields = useValidateBvEnabled();
    const requireCopayCoinsurance = useValidateCopayCoinsuranceEnabled();
    const requireEvFieldsOnContinue = useValidateEvEnabled();

    const analytics = useCoverageAnalytics(
      orderId,
      demographicsData,
      authOnFileDetails,
      submissionData,
      status,
      coverage,
      generalOrderDetails,
    );
    const inactiveInsurance = useInactiveInsuranceWriteBack(
      authOnFileDetails?.reports,
    );
    const happyFlow = useHappyFlowSave({ analytics, orderId, status });

    useEffect(() => {
      if (openedModalId === ModalId.COMMENT_RPA_MODAL_NEW) {
        openModal(ModalId.COMMENT_RPA_MODAL_NEW);
      }
      if (openedModalId === ModalId.HAPPY_FLOW_MODAL) {
        openModal(ModalId.HAPPY_FLOW_MODAL);
      }
    }, [openedModalId]);

    useEffect(() => {
      currentTabIndex?.(activeTabIndex);
    }, [activeTabIndex, currentTabIndex]);

    const coverageTabs = useMemo(() => {
      return coverageInformation.map((entry, index) => {
        const singleCoverageInformation = entry.singleCoverageInformation;
        const tierRow = coverage[index];
        const rawType = tierRow?.coverage_type?.trim();
        const tierLabel = rawType
          ? `${rawType.charAt(0).toUpperCase()}${rawType.slice(1).toLowerCase()}`
          : `Coverage ${index + 1}`;

        const planLabel =
          singleCoverageInformation.payerInformation ||
          tierRow?.payer_name ||
          "Unknown";

        return {
          id: `insurance-tab-${index}`,
          label: `${tierLabel} — ${planLabel}`,
          singleCoverageInformation,
        };
      });
    }, [coverage, coverageInformation]);

    useEffect(() => {
      if (coverageTabs.length > 0 && activeTabIndex >= coverageTabs.length) {
        setActiveTabIndex(0);
      }
    }, [coverageTabs.length, activeTabIndex]);

    const patientIdForEvents =
      demographicsData?.patient_id ?? demographicsData?.patient_mrn ?? "";

    const primaryCoverageRow = coverage.find(
      (c) => c.coverage_type === "primary",
    );

    /**
     * Emits the shared `INSURANCE_TAB_SELECTED` analytics event for a tab.
     *
     * The two call sites intentionally pass different `primaryInsuranceStatus`
     * values: the user tab-click path reports the Redux EV status
     * (`status?.ev_bv?.primary`), while the one-shot initial-load path reports
     * the primary coverage row's raw `active` flag. Both are preserved here.
     */
    const logInsuranceTabSelected = useCallback(
      (tabIndex: number, primaryInsuranceStatus: string) => {
        apiWorkerManager.enqueueAndProcess({
          patient_id: patientIdForEvents,
          order_id: orderId,
          org_id: org_id ?? "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name:
            EventsName.INSURANCE_TAB_SELECTED +
            " (" +
            coverageTabs[tabIndex]?.label +
            ")",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.COVERAGE_SCREEN,
            coverage_type: coverageTabs[tabIndex]?.label ?? "",
            tab_index: tabIndex,
            coverage_status: status?.ev_bv?.primary ?? "",
            primary_insurance_status: primaryInsuranceStatus,
            payer_name: payerName,
          },
        });
      },
      [
        coverageTabs,
        orderId,
        org_id,
        patientIdForEvents,
        payerName,
        status?.ev_bv?.primary,
        user?.email,
        user?.id,
      ],
    );

    const handleInsuranceTabChange = (index: number) => {
      setActiveTabIndex(index);
      logInsuranceTabSelected(index, status?.ev_bv?.primary ?? "");
    };

    useEffect(() => {
      if (coverageTabs.length > 0 && !isEventTriggerRef.current) {
        logInsuranceTabSelected(
          activeTabIndex,
          String(primaryCoverageRow?.active ?? ""),
        );
        isEventTriggerRef.current = true;
      }
    }, [
      activeTabIndex,
      coverageTabs,
      logInsuranceTabSelected,
      primaryCoverageRow?.active,
    ]);

    const activeTabIndexClamped =
      coverageTabs.length === 0
        ? 0
        : Math.min(activeTabIndex, coverageTabs.length - 1);

    const activeCoverageTab = coverageTabs[activeTabIndexClamped];
    const activeCoverageType =
      coverage[activeTabIndexClamped]?.coverage_type?.trim().toLowerCase() ??
      "";
    const activeTierManuallyUpdated = isCoverageRowManuallyUpdated(
      coverage[activeTabIndexClamped],
    );

    const buildContinueValidationSnapshots = useCallback(() => {
      if (!coverageInformation?.length) {
        return [] as EvCoverageContinueValidationEntry[];
      }

      return coverageInformation.flatMap((entry, index) => {
        const snapshot = entry.singleCoverageInformation;
        if (!snapshot) {
          return [];
        }
        const coverageType = coverage[index]?.coverage_type
          ?.trim()
          .toLowerCase();
        if (coverageType !== "primary") {
          return [];
        }
        const resolvedSnapshot =
          index === activeTabIndexClamped
            ? (activeUnifiedContentRef.current?.getValidationSnapshot() ??
              snapshot)
            : snapshot;
        return [{ snapshot: resolvedSnapshot, coverageTabIndex: index }];
      });
    }, [activeTabIndexClamped, coverage, coverageInformation]);

    const handleCoverageSaved = useCallback(() => {
      setHasAttemptedContinue(false);
      setAutoEnterEditOnContinueFailure(false);
      setFocusTreatmentTabIndex(undefined);
      onCoverageSaved?.();
    }, [onCoverageSaved]);

    const handleContinueValidationDismiss = useCallback(() => {
      setHasAttemptedContinue(false);
      setAutoEnterEditOnContinueFailure(false);
      setFocusTreatmentTabIndex(undefined);
    }, []);

    const evCoverageAnalyticsContext = useMemo(
      () => ({
        demographics: demographicsData,
        orderId,
        orgId: org_id ?? "",
        userId: user?.id ?? "",
        userEmail: user?.email ?? "",
        payerName,
        coverageType: activeCoverageType,
      }),
      [
        activeCoverageType,
        demographicsData,
        orderId,
        org_id,
        payerName,
        user?.email,
        user?.id,
      ],
    );

    const handleManualEvEditClicked = useCallback(
      (source: ManualEvEditClickSource) => {
        apiWorkerManager.enqueueAndProcess(
          buildManualEvEditClickedEvent(evCoverageAnalyticsContext, source),
        );
      },
      [evCoverageAnalyticsContext],
    );

    const handleManualEvOverrideSaved = useCallback(() => {
      apiWorkerManager.enqueueAndProcess(
        buildManualEvOverrideSavedEvent(evCoverageAnalyticsContext),
      );
    }, [evCoverageAnalyticsContext]);

    const handleManualEvFieldEdited = useCallback(
      (detail: ManualEvFieldEditedDetail) => {
        apiWorkerManager.enqueueAndProcess(
          buildManualEvFieldEditedEvent(evCoverageAnalyticsContext, detail),
        );
      },
      [evCoverageAnalyticsContext],
    );

    const debouncedManualEvFieldEdited = useDebouncedManualEvFieldEdited(
      handleManualEvFieldEdited,
    );

    const dateOfWork = generalOrderDetails?.status?.date_of_work;

    const validateForContinue =
      useCallback((): ValidateAllEvCoverageTabsResult | null => {
        if (
          !EV_COVERAGE_REQUIRE_FIELDS_ON_SAVE ||
          !requireEvFieldsOnContinue ||
          !shouldRunEvCoverageContinueValidationForDateOfWork(dateOfWork)
        ) {
          return null;
        }

        const snapshots = buildContinueValidationSnapshots();
        if (snapshots.length === 0) {
          return null;
        }

        const result = validateAllEvCoverageTabsForContinue(snapshots, {
          includePlanNameAndMemberId: true,
          requireBvFields,
          requireCopayCoinsurance,
        });

        if (!result.valid && result.firstFailure) {
          setHasAttemptedContinue(true);
          setContinueValidationAttemptId((attemptId) => attemptId + 1);
          setAutoEnterEditOnContinueFailure(
            shouldAutoEnterEditModeOnContinueValidationFailure(result.issues),
          );
          setFocusTreatmentTabIndex(result.firstFailure.treatmentTabIndex);
          setActiveTabIndex(result.firstFailure.coverageTabIndex);
        }

        return result;
      }, [
        buildContinueValidationSnapshots,
        dateOfWork,
        requireBvFields,
        requireCopayCoinsurance,
        requireEvFieldsOnContinue,
      ]);

    useImperativeHandle(ref, () => ({ validateForContinue }), [
      validateForContinue,
    ]);

    const insuranceTierSwitcherProps =
      coverageTabs.length > 0
        ? {
            tabs: coverageTabs.map((t) => ({ id: t.id, label: t.label })),
            activeIndex: activeTabIndexClamped,
            onTabChange: handleInsuranceTabChange,
          }
        : undefined;

    return (
      <>
        {coverageTabs.length > 0 && activeCoverageTab && (
          <div className="created-coverage-tab ev-coverage-tab flex h-full min-h-0 pt-1 md:pt-2.5">
            {activeCoverageTab.singleCoverageInformation ? (
              <EvCoverageUnifiedContent
                ref={activeUnifiedContentRef}
                key={activeTabIndexClamped}
                singleCoverage={activeCoverageTab.singleCoverageInformation}
                orderId={orderId}
                orgId={orgId}
                requireBvFields={requireBvFields}
                requireCopayCoinsurance={requireCopayCoinsurance}
                coverageType={activeCoverageType}
                onCoverageSaved={handleCoverageSaved}
                serviceTypeCodeLabelMap={serviceTypeCodeLabelMap}
                hasAttemptedContinue={hasAttemptedContinue}
                continueValidationAttemptId={continueValidationAttemptId}
                autoEnterEditOnContinueFailure={autoEnterEditOnContinueFailure}
                focusTreatmentTabIndex={focusTreatmentTabIndex}
                inactiveInsuranceProps={{
                  onCommentsChange: inactiveInsurance.handleCommentsChange,
                  queryType: inactiveInsurance.queryType,
                  status: status?.ev_write_back_status ?? "",
                  id: orderId,
                  reports: authOnFileDetails?.reports ?? [],
                  generalOrderDetails: generalOrderDetails,
                }}
                insuranceTierSwitcher={insuranceTierSwitcherProps}
                onManualEvEditClicked={handleManualEvEditClicked}
                onManualEvFieldEdited={debouncedManualEvFieldEdited}
                onManualEvOverrideSaved={handleManualEvOverrideSaved}
                onEditingChange={onEditingChange}
                onContinueValidationDismiss={handleContinueValidationDismiss}
                isManuallyUpdated={activeTierManuallyUpdated}
              />
            ) : (
              <div className="flex h-full min-h-0 w-full flex-col gap-2">
                {insuranceTierSwitcherProps && (
                  <EvCoverageInsuranceTierTabBar
                    {...insuranceTierSwitcherProps}
                  />
                )}
                <EvCoverageEmptyState variant="tierUnavailable" />
              </div>
            )}
          </div>
        )}
        <CoverageModals
          analytics={analytics}
          happyFlow={happyFlow}
          writeBackNotes={inactiveInsurance.writeBackNotes}
          generalOrderDetails={generalOrderDetails}
          authOnFileDetails={authOnFileDetails}
        />
      </>
    );
  },
);

Coverage.displayName = "Coverage";
